import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ChevronLeft, Search, Loader2, Star, MapPin, ImageOff, Filter, CheckCircle, XCircle } from 'lucide-react'
import { getRestaurant, getItems } from '@/api/client'
import { useApi } from '@/hooks/useApi'
import { photoUrl } from '@/lib/photos'
import { AuthedImage } from '@/components/AuthedImage'

export function RestaurantDetail() {
  const { id } = useParams()
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [onlyPhoto, setOnlyPhoto] = useState(false)

  const { data: restaurant, loading: restaurantLoading } = useApi(
    () => getRestaurant(parseInt(id)),
    [id]
  )

  const { data: items, loading: itemsLoading } = useApi(
    () => getItems(parseInt(id), { filter_status: 'all' }),
    [id]
  )

  const categories = useMemo(() => {
    const set = new Set()
    ;(items || []).forEach((i) => { if (i.category) set.add(i.category) })
    return ['', ...Array.from(set).sort()]
  }, [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (items || []).filter((i) => {
      if (category && i.category !== category) return false
      if (onlyPhoto && !i.photo_exists) return false
      if (!q) return true
      return [i.name, i.description, i.ingredients, i.extras]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    })
  }, [items, search, category, onlyPhoto])

  const grouped = useMemo(() => {
    const map = new Map()
    filtered.forEach((i) => {
      const key = i.category || 'Uncategorized'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(i)
    })
    return Array.from(map.entries())
  }, [filtered])

  if (restaurantLoading) {
    return <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto my-16" />
  }

  if (!restaurant) {
    return <div className="text-center py-16 text-gray-500">Restaurant not found</div>
  }

  const photoCount = (items || []).filter((i) => i.photo_exists).length

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <Link to="/" className="p-2 mt-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{restaurant.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-1">
            {restaurant.location && (
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{restaurant.location}</span>
            )}
            {restaurant.rating ? (
              <span className="flex items-center gap-1">
                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                {restaurant.rating}
                {restaurant.reviews ? <span className="text-gray-400">({restaurant.reviews} reviews)</span> : null}
              </span>
            ) : null}
            {restaurant.price_range && <span>{restaurant.price_range}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 text-sm text-gray-600 mt-2">
            <span>{restaurant.item_count} items</span>
            <span className="text-green-600">{photoCount} with photo</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white max-w-xs"
          >
            <option value="">All categories</option>
            {categories.filter(Boolean).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={onlyPhoto}
            onChange={(e) => setOnlyPhoto(e.target.checked)}
            className="h-4 w-4 text-orange-500 rounded"
          />
          With photo only
        </label>
      </div>

      {itemsLoading && !items ? (
        <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto my-16" />
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-4">{filtered.length} items shown</p>
          {grouped.map(([cat, catItems]) => (
            <section key={cat} className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                {cat}
                <span className="text-sm font-normal text-gray-400">({catItems.length})</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {catItems.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ))}
          {grouped.length === 0 && (
            <div className="text-center py-12 text-gray-500">No items match your filters</div>
          )}
        </>
      )}
    </div>
  )
}

function ItemCard({ item }) {
  const src = photoUrl(item.image_path)
  return (
    <Link
      to={`/item/${item.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all overflow-hidden flex flex-col"
    >
      <div className="aspect-square bg-gray-100 overflow-hidden relative">
        {item.photo_exists && src ? (
          <AuthedImage
            src={src}
            alt={item.name}
            className="w-full h-full object-cover"
            fallback={<ImageOff className="h-10 w-10" />}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <ImageOff className="h-10 w-10" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          {item.available ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
              <CheckCircle className="h-3 w-3" /> Available
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
              <XCircle className="h-3 w-3" /> Unavailable
            </span>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-900">{item.name}</h3>
          {item.price !== null && item.price !== '' && (
            <span className="text-orange-600 font-semibold whitespace-nowrap">{item.price}</span>
          )}
        </div>
        {item.category && (
          <span className="self-start text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{item.category}</span>
        )}
        {item.description && (
          <p className="text-sm text-gray-600 line-clamp-3">{item.description}</p>
        )}
        {item.ingredients && (
          <div className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">Ingredients: </span>
            {item.ingredients}
          </div>
        )}
        {item.extras && (
          <div className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">Extras: </span>
            {item.extras}
          </div>
        )}
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-gray-400">
          {item.status && <span>Status: {item.status}</span>}
          {item.tries > 0 && <span>tries: {item.tries}</span>}
        </div>
      </div>
    </Link>
  )
}
